import {
  Box,
  Button,
  Inline,
  Spread,
  Stack,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import { useEffect, type FC } from 'react';
import translations from './.vocab';
import { Display } from 'src/libs/Display';

interface JobPlacementAlertProps {
  jobTitle: string;
  jobLocations: string;
  openJobPlacementDialog: () => void;
  trackJobPlacementBannerDisplay: () => void;
  trackBannerContentPressedEvent: () => void;
}

export const JobPlacementAlert: FC<JobPlacementAlertProps> = ({
  jobTitle,
  jobLocations,
  openJobPlacementDialog,
  trackJobPlacementBannerDisplay,
  trackBannerContentPressedEvent,
}) => {
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();

  useEffect(() => {
    trackJobPlacementBannerDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box marginY="medium">
      <Display desktop tablet>
        <Box
          style={{
            backgroundColor: '#F0F7FE',
            height: '200px',
          }}
          borderRadius="xlarge"
          padding="large"
        >
          <Box
            display="flex"
            width="full"
            justifyContent="spaceBetween"
            height="full"
          >
            <Spread
              direction="vertical"
              space={
                responsiveValue({
                  wide: 'small',
                  desktop: 'small',
                  tablet: 'small',
                  mobile: 'medium',
                })!
              }
            >
              <Text size="large" weight="medium">
                {t(
                  'Have you placed a candidate for {jobTitle} in {jobLocations} through SEEK?',
                  {
                    jobTitle,
                    jobLocations,
                  },
                )}
              </Text>
              <Stack
                space={
                  responsiveValue({
                    wide: 'xsmall',
                    desktop: 'xsmall',
                    tablet: 'xsmall',
                    mobile: 'xxsmall',
                  })!
                }
              >
                <Text>
                  {t('Your feedback helps improve the reports we offer.')}
                </Text>
              </Stack>

              <Inline space="none">
                <Button
                  onClick={() => {
                    trackBannerContentPressedEvent();
                    openJobPlacementDialog();
                  }}
                  variant="solid"
                >
                  {t('Let us know')}
                </Button>
              </Inline>
            </Spread>

            <Box>
              <Box
                alignItems="center"
                display="flex"
                flexGrow={1}
                flexShrink={0}
                width="full"
                paddingLeft="large"
                height="full"
              >
                <Box
                  component="img"
                  role="img"
                  src="https://seekcdn.com/cm-ui/images/CandiMan-JobPlacement.png"
                  style={{ height: 'auto', margin: 'auto', width: '160px' }}
                  alignItems="center"
                  display="flex"
                  flexGrow={1}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Display>

      <Display mobile>
        <Box
          style={{
            backgroundColor: '#F0F7FE',
          }}
          borderRadius="xlarge"
          padding="gutter"
        >
          <Box
            display="flex"
            width="full"
            justifyContent="spaceBetween"
            height="full"
          >
            <Spread
              direction="vertical"
              space={
                responsiveValue({
                  wide: 'small',
                  desktop: 'small',
                  tablet: 'small',
                  mobile: 'medium',
                })!
              }
            >
              <Text weight="medium">
                {t(
                  'Have you placed a candidate for {jobTitle} in {jobLocations} through SEEK?',
                  {
                    jobTitle,
                    jobLocations,
                  },
                )}
              </Text>
              <Stack
                space={
                  responsiveValue({
                    wide: 'xsmall',
                    desktop: 'xsmall',
                    tablet: 'xsmall',
                    mobile: 'xxsmall',
                  })!
                }
              >
                <Text size="small">
                  {t('Your feedback helps improve the reports we offer.')}
                </Text>
              </Stack>

              <Inline space="none">
                <Button
                  size="small"
                  onClick={() => {
                    trackBannerContentPressedEvent();
                    openJobPlacementDialog();
                  }}
                  variant="solid"
                >
                  {t('Let us know')}
                </Button>
              </Inline>
            </Spread>
            <></>
          </Box>
        </Box>
      </Display>
    </Box>
  );
};
